import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import FileUpload from "@/components/admin/FileUpload";
import { FileGrid } from "@/components/admin/PrivateFile";
import {
  WATCH_STATUSES, AUTH_STATUSES, titleCase, canSeeFinancials,
} from "@/lib/format";
import { toast } from "sonner";
import { ArrowLeft, Lock, ShieldCheck } from "lucide-react";

const EMPTY = {
  brand: "", model: "", reference_number: "", year: "", case_size: "", material: "",
  movement: "", dial: "", bracelet_strap: "", condition: "", box_papers: "", service_history: "",
  authentication_status: "not_started", price: "", price_on_request: false, deposit_required: false,
  deposit_amount: "", public_description: "", public_photos: [], video_url: "",
  status: "available", top_pick: false,
  dealer_cost: "", profit_margin: "", min_acceptable_price: "",
  source_seller: "", seller_contact: "", buyer_contact: "", serial_number: "",
  weight: "", measurements: "", internal_notes: "",
  purchase_documents: [], authentication_documents: [],
};

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function SectionTitle({ children, locked }) {
  return (
    <div className="flex items-center gap-2 mt-8 mb-3">
      <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--text-2)]">{children}</h2>
      {locked && <Badge variant="outline" className="bg-[var(--panel)] text-[#c9a86a] border-[var(--border-2)] text-[10px]"><Lock size={10} className="mr-1" />PRIVATE</Badge>}
    </div>
  );
}

function TextField({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-[var(--text-2)]">{label}</Label>
      <Input type={type} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className="bg-[var(--panel)]" data-testid={testId} />
    </div>
  );
}

export default function AdminWatchForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const showFin = canSeeFinancials(user?.role);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/watches/${id}`).then((r) => {
      setForm({ ...EMPTY, ...r.data });
      setLoading(false);
    }).catch(() => { toast.error("Watch not found"); navigate("/admin/watches"); });
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.brand || !form.model) { toast.error("Brand and Model are required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      price: numOrNull(form.price),
      deposit_amount: numOrNull(form.deposit_amount),
      dealer_cost: numOrNull(form.dealer_cost),
      profit_margin: numOrNull(form.profit_margin),
      min_acceptable_price: numOrNull(form.min_acceptable_price),
    };
    if (!showFin) { delete payload.dealer_cost; delete payload.profit_margin; delete payload.min_acceptable_price; }
    try {
      if (isEdit) await api.put(`/admin/watches/${id}`, payload);
      else await api.post("/admin/watches", payload);
      toast.success(isEdit ? "Watch updated" : "Watch created");
      navigate("/admin/watches");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Quick actions (edit only)
  const quick = async (fn, msg) => {
    try { await fn(); toast.success(msg); const r = await api.get(`/admin/watches/${id}`); setForm({ ...EMPTY, ...r.data }); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="p-8 text-[var(--muted)]">Loading…</div>;

  return (
    <div data-testid="watch-form" className="max-w-4xl">
      <button onClick={() => navigate("/admin/watches")} className="flex items-center gap-2 text-sm text-[var(--text-2)] hover:text-[var(--text)] mb-4">
        <ArrowLeft size={16} /> Back to Watches
      </button>
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-semibold">{isEdit ? `${form.brand} ${form.model}` : "Add Watch"}</h1>
        <Button onClick={save} disabled={saving} data-testid="save-watch-btn" className="bg-[var(--panel-2)] hover:bg-[var(--border-2)] text-[#c9a86a]">
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Watch"}
        </Button>
      </div>

      {isEdit && (
        <Card className="p-4 mb-4 border-[var(--border-lch)] bg-[var(--panel-2)]">
          <div className="text-xs uppercase tracking-wide text-[var(--text-2)] mb-2">Quick Actions</div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => quick(() => api.patch(`/admin/watches/${id}/top-pick`, { top_pick: !form.top_pick }), "Updated")}>
              {form.top_pick ? "Remove Top Pick" : "Mark Top Pick"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => quick(() => api.patch(`/admin/watches/${id}/status`, { status: "available" }), "Available")}>Available</Button>
            <Button size="sm" variant="outline" onClick={() => quick(() => api.patch(`/admin/watches/${id}/status`, { status: "reserved" }), "Reserved")}>Reserved</Button>
            <Button size="sm" variant="outline" onClick={() => quick(() => api.patch(`/admin/watches/${id}/status`, { status: "sold" }), "Sold")}>Sold</Button>
            <Button size="sm" variant="outline" onClick={() => quick(() => api.patch(`/admin/watches/${id}/authentication`, { authentication_status: "pending" }), "Sent to authentication")}>Send to Auth</Button>
            <Button size="sm" variant="outline" onClick={() => quick(() => api.patch(`/admin/watches/${id}/authentication`, { authentication_status: "passed" }), "Auth passed")}><ShieldCheck size={14} className="mr-1" />Auth Passed</Button>
            <Button size="sm" variant="outline" onClick={() => quick(() => api.patch(`/admin/watches/${id}/authentication`, { authentication_status: "failed" }), "Auth failed")}>Auth Failed</Button>
            <Button size="sm" variant="outline" onClick={() => quick(() => api.post(`/admin/watches/${id}/request-deposit`), "Deposit requested")}>Request Deposit</Button>
            <Button size="sm" variant="outline" onClick={() => quick(() => api.post(`/admin/watches/${id}/generate-dossier`), "Dossier generated")}>Generate Dossier</Button>
          </div>
        </Card>
      )}

      <Card className="p-6 border-[var(--border-lch)]">
        <SectionTitle>Identity</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Brand *" value={form.brand} onChange={(v) => set("brand", v)} testId="f-brand" />
          <TextField label="Model *" value={form.model} onChange={(v) => set("model", v)} testId="f-model" />
          <TextField label="Reference Number" value={form.reference_number} onChange={(v) => set("reference_number", v)} testId="f-ref" />
          <TextField label="Year" value={form.year} onChange={(v) => set("year", v)} />
        </div>

        <SectionTitle>Specifications</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Case Size" value={form.case_size} onChange={(v) => set("case_size", v)} />
          <TextField label="Material" value={form.material} onChange={(v) => set("material", v)} />
          <TextField label="Movement" value={form.movement} onChange={(v) => set("movement", v)} />
          <TextField label="Dial" value={form.dial} onChange={(v) => set("dial", v)} />
          <TextField label="Bracelet / Strap" value={form.bracelet_strap} onChange={(v) => set("bracelet_strap", v)} />
          <TextField label="Condition" value={form.condition} onChange={(v) => set("condition", v)} />
          <TextField label="Box & Papers" value={form.box_papers} onChange={(v) => set("box_papers", v)} />
          <TextField label="Service History" value={form.service_history} onChange={(v) => set("service_history", v)} />
        </div>

        <SectionTitle>Listing & Pricing</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="bg-[var(--panel)]" data-testid="f-status"><SelectValue /></SelectTrigger>
              <SelectContent>{WATCH_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-[var(--text-2)]">Authentication Status</Label>
            <Select value={form.authentication_status} onValueChange={(v) => set("authentication_status", v)}>
              <SelectTrigger className="bg-[var(--panel)]" data-testid="f-auth"><SelectValue /></SelectTrigger>
              <SelectContent>{AUTH_STATUSES.map((s) => <SelectItem key={s} value={s}>{titleCase(s)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={form.price_on_request} onCheckedChange={(v) => set("price_on_request", v)} data-testid="f-por" />
            <Label className="text-sm">Price on Request</Label>
          </div>
          <TextField label="Price (USD)" type="number" value={form.price} onChange={(v) => set("price", v)} placeholder="e.g. 16500" testId="f-price" />
          <div className="flex items-center gap-3 pt-6">
            <Switch checked={form.deposit_required} onCheckedChange={(v) => set("deposit_required", v)} />
            <Label className="text-sm">Deposit Required</Label>
          </div>
          <TextField label="Deposit Amount (USD)" type="number" value={form.deposit_amount} onChange={(v) => set("deposit_amount", v)} />
          <div className="flex items-center gap-3">
            <Switch checked={form.top_pick} onCheckedChange={(v) => set("top_pick", v)} data-testid="f-toppick" />
            <Label className="text-sm">Top Pick (Homepage)</Label>
          </div>
          <TextField label="Video / 360 URL" value={form.video_url} onChange={(v) => set("video_url", v)} />
        </div>
        <div className="mt-4 space-y-1.5">
          <Label className="text-xs text-[var(--text-2)]">Public Description</Label>
          <Textarea value={form.public_description ?? ""} onChange={(e) => set("public_description", e.target.value)} rows={3} className="bg-[var(--panel)]" data-testid="f-desc" />
        </div>

        <SectionTitle>Public Photos</SectionTitle>
        <div className="space-y-3">
          <FileGrid files={form.public_photos} onRemove={(f) => set("public_photos", form.public_photos.filter((x) => x.id !== f.id))} />
          <FileUpload visibility="public" category="watch_public" label="Add Public Photos" testId="upload-public-photo"
            onUploaded={(fs) => set("public_photos", [...form.public_photos, ...fs])} />
        </div>

        <SectionTitle locked>Private · Operational</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Serial Number" value={form.serial_number} onChange={(v) => set("serial_number", v)} testId="f-serial" />
          <TextField label="Weight" value={form.weight} onChange={(v) => set("weight", v)} />
          <TextField label="Measurements" value={form.measurements} onChange={(v) => set("measurements", v)} />
          <TextField label="Source / Seller" value={form.source_seller} onChange={(v) => set("source_seller", v)} />
          <TextField label="Seller Contact" value={form.seller_contact} onChange={(v) => set("seller_contact", v)} />
          <TextField label="Buyer Contact" value={form.buyer_contact} onChange={(v) => set("buyer_contact", v)} />
        </div>
        <div className="mt-4 space-y-1.5">
          <Label className="text-xs text-[var(--text-2)]">Internal Notes</Label>
          <Textarea value={form.internal_notes ?? ""} onChange={(e) => set("internal_notes", e.target.value)} rows={2} className="bg-[var(--panel)]" data-testid="f-notes" />
        </div>

        {showFin && (
          <>
            <SectionTitle locked>Financials</SectionTitle>
            <div className="grid sm:grid-cols-3 gap-4">
              <TextField label="Dealer Cost (USD)" type="number" value={form.dealer_cost} onChange={(v) => set("dealer_cost", v)} testId="f-cost" />
              <TextField label="Profit Margin (USD)" type="number" value={form.profit_margin} onChange={(v) => set("profit_margin", v)} />
              <TextField label="Min Acceptable Price (USD)" type="number" value={form.min_acceptable_price} onChange={(v) => set("min_acceptable_price", v)} />
            </div>
          </>
        )}

        <SectionTitle locked>Private Documents</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Label className="text-xs text-[var(--text-2)]">Authentication Documents</Label>
            <FileGrid files={form.authentication_documents} onRemove={(f) => set("authentication_documents", form.authentication_documents.filter((x) => x.id !== f.id))} />
            <FileUpload visibility="private" accept="image/*,application/pdf" category="auth_doc" label="Upload Auth Docs" testId="upload-auth-doc"
              onUploaded={(fs) => set("authentication_documents", [...form.authentication_documents, ...fs])} />
          </div>
          <div className="space-y-3">
            <Label className="text-xs text-[var(--text-2)]">Purchase Documents</Label>
            <FileGrid files={form.purchase_documents} onRemove={(f) => set("purchase_documents", form.purchase_documents.filter((x) => x.id !== f.id))} />
            <FileUpload visibility="private" accept="image/*,application/pdf" category="purchase_doc" label="Upload Purchase Docs"
              onUploaded={(fs) => set("purchase_documents", [...form.purchase_documents, ...fs])} />
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-[var(--panel-2)] hover:bg-[var(--border-2)] text-[#c9a86a]">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Watch"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
